'use strict';
/* Shopify owns the persistent sign-in cookie. Bearer/refresh tokens remain in
 * memory only. A page return uses OIDC prompt=none, never a parallel login or a
 * JavaScript-readable persistent auth cookie. Shopify controls session expiry. */
(() => {
  const CLIENT = '1698512b-c882-4c70-8e16-13bb4fbf7231'; // Public client ID.
  const ORIGIN = 'https://mycoffeeco.com';
  const ISSUER = 'https://shopify.com/authentication/65432387693';
  const CALLBACK = ORIGIN + '/pages/my-account?view=customer-dashboard';
  const TRANSACTION = 'mcc-oauth-transaction';
  const PANELS = /^#(dashboard|orders|profile|wishlist|recent|subscription|security)$/;
  const b64 = bytes => btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const random = () => b64(crypto.getRandomValues(new Uint8Array(32)));
  const decode = value => Uint8Array.from(atob(value.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));

  window.MCCCustomerSession = {create({verificationKeys, onGate, onConnecting, onReady}) {
    let auth, api, discovery, accessToken, refreshToken, idToken, subject, nonce;
    let expiresAt = 0, generation = 0, navigating = false, renewal, booting;
    const channel = typeof BroadcastChannel === 'function' ? new BroadcastChannel('mcc-customer-session') : null;
    function reset() {
      generation++;
      accessToken = refreshToken = idToken = subject = nonce = null;
      expiresAt = 0;
    }
    function gate(message) { reset(); navigating = false; onGate(message); }
    function trusted(value) {
      const u = new URL(value);
      if (u.protocol !== 'https:' || u.username || u.password || u.hash ||
          !(u.origin === ORIGIN || (u.origin === 'https://shopify.com' &&
            (u.pathname.startsWith('/authentication/65432387693/') || u.pathname.startsWith('/65432387693/'))))) {
        throw Error('Account configuration could not be verified.');
      }
      return u.href;
    }
    async function json(url, options = {}) {
      const response = await fetch(url, {...options, cache:'no-store', referrerPolicy:'no-referrer', signal:AbortSignal.timeout(20000)});
      if (!response.ok) {
        const error = Error(response.status === 401 ? 'Your session has ended. Please sign in again.' : 'Unable to connect right now. Please try again.');
        error.status = response.status;
        throw error;
      }
      return response.json();
    }
    async function discover() {
      if (!discovery) discovery = (async () => {
        const [a,c] = await Promise.all([json(ORIGIN+'/.well-known/openid-configuration'),json(ORIGIN+'/.well-known/customer-account-api')]);
        if (a.issuer !== ISSUER) throw Error('Account provider could not be verified.');
        for (const key of ['authorization_endpoint','token_endpoint','end_session_endpoint','jwks_uri']) a[key] = trusted(a[key]);
        api = trusted(c.graphql_api); auth = a;
      })().catch(error => { discovery = null; throw error; });
      return discovery;
    }
    async function verifyIdentity(token, expectedNonce, expectedSubject, isRefresh = false) {
      if (typeof token !== 'string') throw Error('Sign-in could not be verified.');
      const parts = token.split('.');
      if (parts.length !== 3) throw Error('Sign-in could not be verified.');
      const header = JSON.parse(new TextDecoder().decode(decode(parts[0])));
      const claims = JSON.parse(new TextDecoder().decode(decode(parts[1])));
      const now = Date.now()/1000;
      // nonce may be omitted in a refresh ID token (OIDC); if present it must match.
      // Support codes identify the failed check without exposing token contents.
      const fail = reason => { throw Error('Sign-in could not be verified ('+reason+').'); };
      if (header.alg !== 'RS256') fail('signature algorithm');
      if (claims.iss !== ISSUER) fail('provider');
      if (![].concat(claims.aud).includes(CLIENT) || (claims.azp && claims.azp !== CLIENT) ||
          (Array.isArray(claims.aud) && claims.aud.length > 1 && claims.azp !== CLIENT)) fail('client');
      if ((!isRefresh || claims.nonce !== undefined) && claims.nonce !== expectedNonce) fail('sign-in request');
      // This Shopify client returns a numeric subject. Accept an exact positive
      // safe integer as well as a string; do not coerce or round identity values.
      const validSubject = (typeof claims.sub === 'string' && claims.sub.length > 0) ||
        (Number.isSafeInteger(claims.sub) && claims.sub > 0);
      if (!validSubject || (expectedSubject != null && claims.sub !== expectedSubject)) fail('account identity');
      if (!Number.isFinite(claims.exp) || claims.exp <= now) fail('expiry');
      if (claims.nbf !== undefined && (!Number.isFinite(claims.nbf) || claims.nbf > now+60)) fail('validity time');
      if (claims.iat !== undefined && (!Number.isFinite(claims.iat) || claims.iat > now+60)) fail('issue time');
      // The issuer JWKS endpoint lacks browser CORS. Keep the existing validated
      // theme-hosted public key; unknown/rotated keys fail closed, never skip RSA.
      const keyUrl = new URL(verificationKeys, ORIGIN);
      if (keyUrl.protocol !== 'https:' || keyUrl.username || keyUrl.password ||
          ![ORIGIN,'https://cdn.shopify.com'].includes(keyUrl.origin)) throw Error('Account verification is unavailable. Please use Open Shopify account.');
      const jwks = await json(keyUrl.href);
      const jwk = jwks.keys?.find(k => k.kid === header.kid && k.kty === 'RSA' && k.alg === 'RS256' && k.use === 'sig');
      if (!jwk) throw Error('Account verification needs an update. Please use Open Shopify account.');
      const key = await crypto.subtle.importKey('jwk',jwk,{name:'RSASSA-PKCS1-v1_5',hash:'SHA-256'},false,['verify']);
      if (!await crypto.subtle.verify('RSASSA-PKCS1-v1_5',key,decode(parts[2]),new TextEncoder().encode(parts[0]+'.'+parts[1]))) fail('signature');
      return claims;
    }
    async function signIn({silent = false} = {}) {
      if (navigating) return;
      navigating = true;
      reset();
      const stamp = generation;
      onConnecting(silent ? 'Restoring your Shopify session…' : 'Opening secure Shopify sign-in…');
      try {
        await discover();
        if (stamp !== generation) return;
        const transaction = {state:random(),nonce:random(),verifier:random(),created:Date.now(),silent,panel:PANELS.test(location.hash)?location.hash:'#dashboard'};
        // Short-lived PKCE transaction only: no customer data or auth tokens.
        sessionStorage.setItem(TRANSACTION,JSON.stringify(transaction));
        const challenge = b64(new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(transaction.verifier))));
        if (stamp !== generation) return;
        const url = new URL(auth.authorization_endpoint);
        url.search = new URLSearchParams({client_id:CLIENT,scope:'openid email customer-account-api:full',response_type:'code',redirect_uri:CALLBACK,state:transaction.state,nonce:transaction.nonce,code_challenge:challenge,code_challenge_method:'S256'});
        if (silent) url.searchParams.set('prompt','none');
        location.assign(url.href);
      } catch (error) {
        if (stamp === generation) gate(error.name === 'SecurityError' ? 'Allow session storage to sign in securely, or use Open Shopify account.' : error.message);
      }
    }
    function validateTokens(tokens) {
      const lifetime = Number(tokens.expires_in);
      if (typeof tokens.access_token !== 'string' || !tokens.access_token || !Number.isFinite(lifetime) || lifetime <= 0 || lifetime > 2147483) throw Error('Sign-in could not be completed.');
      return Date.now()+lifetime*1000;
    }
    async function ensureAccess() {
      if (navigating || !accessToken) throw Error('Please sign in to view your account.');
      if (Date.now()+30000 < expiresAt) return;
      if (!refreshToken) {
        await signIn({silent:true});
        throw Error('Restoring your session. Please retry your change after your account reopens.');
      }
      if (!renewal) {
        const stamp = generation;
        renewal = (async () => {
          try {
            const tokens = await json(auth.token_endpoint,{method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},body:new URLSearchParams({grant_type:'refresh_token',client_id:CLIENT,refresh_token:refreshToken})});
            const expiry = validateTokens(tokens);
            if (tokens.id_token) await verifyIdentity(tokens.id_token,nonce,subject,true);
            if (stamp !== generation) throw Error('Your session has ended.');
            accessToken = tokens.access_token;
            refreshToken = tokens.refresh_token || refreshToken;
            idToken = tokens.id_token || idToken;
            expiresAt = expiry;
          } catch (error) {
            if (stamp === generation && [400,401].includes(error.status)) {
              await signIn({silent:true});
            } else if (stamp === generation) gate(error.message);
            throw error;
          }
        })().finally(() => { renewal = null; });
      }
      await renewal;
    }
    async function query(query, variables = {}) {
      await ensureAccess();
      const stamp = generation;
      try {
        const result = await json(api,{method:'POST',headers:{'Content-Type':'application/json',Authorization:accessToken},body:JSON.stringify({query,variables})});
        if (stamp !== generation) throw Error('Your session has ended.');
        if (result.errors?.length || !result.data) {
          if (result.errors?.some(e => ['UNAUTHENTICATED','UNAUTHORIZED'].includes(e.extensions?.code))) gate('Your session has ended. Please sign in again.');
          throw Error('Your account data could not be loaded. Please try again.');
        }
        return result.data;
      } catch (error) {
        // Do not replay a failed mutation or start an OAuth loop after a 401.
        if (stamp === generation && error.status === 401) gate(error.message);
        throw error;
      }
    }
    async function boot() {
      if (booting) return booting;
      booting = (async () => {
        const params = new URLSearchParams(location.search);
        const code = params.get('code'), state = params.get('state'), error = params.get('error');
        const callback = params.has('code') || params.has('state') || params.has('error');
        // Scrub OAuth values synchronously, before discovery or any account load.
        if (callback) history.replaceState(null,'',CALLBACK+location.hash);
        reset(); navigating = false;
        const stamp = generation;
        onConnecting('Restoring your Shopify session…');
        try {
          if (location.origin !== ORIGIN) throw Error('Please open your account on mycoffeeco.com.');
          if (!callback) { await signIn({silent:true}); return; }
          const raw = sessionStorage.getItem(TRANSACTION);
          sessionStorage.removeItem(TRANSACTION); // One-time transaction, including OAuth errors.
          const tx = raw ? JSON.parse(raw) : null;
          if (!tx || typeof state !== 'string' || !state || tx.state !== state ||
              typeof tx.created !== 'number' || !Number.isFinite(tx.created) || Date.now()-tx.created > 600000 || tx.created > Date.now()+30000 ||
              typeof tx.verifier !== 'string' || tx.verifier.length < 43 || typeof tx.nonce !== 'string' || !tx.nonce) throw Error('This sign-in link has expired. Please sign in again.');
          if (PANELS.test(tx.panel)) history.replaceState(null,'',CALLBACK+tx.panel);
          if (error) {
            gate(tx.silent && ['login_required','interaction_required','consent_required'].includes(error) ? 'Sign in once with Shopify to view your account. Future visits reuse your active Shopify session.' : 'Sign-in was not completed. Please try again.');
            return; // Never automatically retry a silent denial.
          }
          if (!code) throw Error('Sign-in was not completed. Please try again.');
          await discover();
          const tokens = await json(auth.token_endpoint,{method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},body:new URLSearchParams({grant_type:'authorization_code',client_id:CLIENT,redirect_uri:CALLBACK,code,code_verifier:tx.verifier})});
          const expiry = validateTokens(tokens);
          const claims = await verifyIdentity(tokens.id_token,tx.nonce);
          if (stamp !== generation) return;
          accessToken = tokens.access_token; refreshToken = tokens.refresh_token || null;
          idToken = tokens.id_token; expiresAt = expiry; subject = claims.sub; nonce = tx.nonce;
          await onReady();
        } catch (error) { if (stamp === generation) gate(error.message); }
      })().finally(() => { booting = null; });
      return booting;
    }
    async function logout() {
      const hint = idToken;
      gate('You have signed out.');
      try { sessionStorage.removeItem(TRANSACTION); sessionStorage.removeItem('mcc-wishlist-intent'); } catch {}
      channel?.postMessage('logout'); // No tokens or personal details shared between tabs.
      await discover();
      const url = new URL(auth.end_session_endpoint);
      if (hint) url.searchParams.set('id_token_hint',hint);
      url.searchParams.set('post_logout_redirect_uri',ORIGIN+'/');
      location.assign(url.href);
    }
    if (channel) channel.onmessage = event => { if (event.data === 'logout') gate('You have signed out in another tab.'); };
    const onPageShow = event => { if (event.persisted) boot(); };
    window.addEventListener('pageshow',onPageShow);
    return {boot,signIn,query,logout,destroy() {reset();channel?.close();window.removeEventListener('pageshow',onPageShow);}};
  }};
})();
